import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A variation of the area chart that displays multiple data series stacked on top of each
          other, with the area beneath each series filled to show its contribution to the total. It
          is particularly useful for visualizing cumulative trends and proportions across categories
          over time. However, it can become challenging to interpret individual series when there
          are many layers or when values fluctuate significantly.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Stacked area charts are perfect for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Proportional Contributions:</strong> For showing how different
            categories contribute to the total over time, such as market share by company, budget
            distribution, or energy source usage.
          </li>
          <li>
            <strong>Tracking Multiple Variables Simultaneously:</strong> For comparing the trends of
            multiple variables, where each category&apos;s individual growth or decline is visible
            while still showing the overall trend, such as tracking various sales channels or
            product categories.
          </li>
          <li>
            <strong>Understanding Cumulative Trends and Patterns:</strong> For visualizing
            cumulative data, with the added benefit of revealing how different components accumulate
            over time, such as total budget split by regions or departments.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          While stacked area charts are effective for visualizing data, they have certain
          limitations to consider:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Individual Series:</strong> As multiple series are
            stacked on top of one another, it can be challenging to compare the values of individual
            categories, especially when the data fluctuates within smaller ranges.
          </li>
          <li>
            <strong>Overlapping and Clutter:</strong> With too many categories or complex data,
            stacked area charts can become cluttered, making it difficult to distinguish between the
            different series and reducing the clarity of the overall trend.
          </li>
          <li>
            <strong>Issues with Negative Values:</strong> Stacked area charts are not suitable for
            displaying negative values, as they are designed to show cumulative data, and negative
            data can distort the visual representation or create confusion.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is vital in stacked area charts to enhance readability and highlight the
          relationships between data series. Using distinct, contrasting colors for each layer helps
          differentiate between categories, making it easier to interpret their individual
          contributions to the total. A consistent color palette across the chart ensures visual
          coherence, while gradual color transitions can represent the intensity or magnitude of
          changes over time. It’s also important to avoid using too many colors to prevent clutter
          and confusion, especially when the chart contains numerous layers or fluctuating values.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['sequential', 'diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          While stacked area charts are helpful for visualizing cumulative data, they come with
          certain challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Individual Series:</strong> Since the data is stacked,
            it can be hard to compare individual data series, especially if the values are close
            together or fluctuate in similar ranges.
          </li>
          <li>
            <strong>Overlapping Data:</strong> With many categories or series, stacked area charts
            can become cluttered, making it challenging to distinguish between the different layers
            and understand the data’s nuances.
          </li>
          <li>
            <strong>Misleading for Small Variations:</strong> Stacked area charts may not
            effectively highlight small variations in data, particularly for lower series that may
            be obscured by the layers above them, potentially leading to missed insights.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
